import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import * as cl from "classnames"
import * as React from "react"
import AxeNumberField from "./axe-number-field"
import AxeAnalytiqueField from "./axe-analytique-field"
import BudgetField from "./budget-field"
import CollectiviteField from "./collectivite-field"
import ServerAddressField from "./server-address-field"
import ServerPortField from "./server-port-field"
import Notification, { NotificationLevel } from "../../../../notification"
import * as Model from "../../../../../model"
import { checkServerExist } from "../../../../../service/sfp/berger-levrault-ws-service"

export default class BergerLevraultConnector
extends React.Component<Props, State> {
	private abortController = new AbortController()

	constructor(props: Readonly<Props>) {
		super(props)
		this.state = {
			collectivites: [],
			connectionStatus: null,
			error: null,
			fetchingCollectivites: false,
		}
	}

	componentDidMount() {
		this.forceFetchingCollectivites(this.props)
	}

	componentWillUnmount() {
		this.abortController.abort()
	}

	render(): JSX.Element {
		return <div className="box">
			<ServerAddressField
			loading={this.props.loading}
			value={this.props.settings.server}
			onChange={s => this.handleServerAddressChange(s)}/>

			<ServerPortField
			loading={this.props.loading}
			value={this.props.settings.port}
			onChange={p => this.handleServerPortChange(p)}/>

			{this.renderError()}

			{this.renderConnectionButton()}

			<CollectiviteField
			collectivites={this.state.collectivites}
			loading={this.props.loading || this.state.fetchingCollectivites}
			value={this.props.settings.idCollectiviteGF}
			onChange={c => this.handleCollectiviteChange(c)}/>

			<AxeNumberField
			loading={this.props.loading || this.state.fetchingCollectivites}
			value={this.props.settings.axe}
			onChange={a => this.handleAxeNumberChange(a)}/>

			<AxeAnalytiqueField
			idCollectivite={this.props.settings.idCollectiviteGF}
			loading={this.props.loading || this.state.fetchingCollectivites}
			serverAddress={this.props.settings.server}
			serverPort={this.props.settings.port}
			value={this.props.settings.idAxe}
			onChange={a => this.handleAxeAnalytiqueChange(a)}/>

			<BudgetField
			loading={this.props.loading || this.state.fetchingCollectivites}
			idCollectivite={this.props.settings.idCollectiviteGF}
			serverAddress={this.props.settings.server}
			serverPort={this.props.settings.port}
			value={this.props.settings.budget}
			onChange={b => this.handleBudgetChange(b)}/>
		</div>
	}

	private forceFetchingCollectivites(props: Readonly<Props>): void {
		if (!(props.settings.server && props.settings.port)) return

		this.abortController.abort()
		this.abortController = new AbortController()

		this.setState({ fetchingCollectivites: true }, () => {
			checkServerExist(
				props.settings.server,
				props.settings.port,
				this.abortController.signal
			)
			.then(collectivites => {
				this.setState({
					connectionStatus: ConnectionStatus.Success,
					fetchingCollectivites: false,
					collectivites
				})
			})
			.catch(reason => {
				let connectionStatus = ConnectionStatus.Fail
				let error = reason

				if (reason instanceof DOMException && reason.code === 20) {
					connectionStatus = null
					error = null
				}

				this.setState({ connectionStatus, error, fetchingCollectivites: false })
			})
		})
	}

	private handleAxeAnalytiqueChange(idAxe: number): void {
		this.props.onChange({ ...this.props.settings, idAxe })
	}

	private handleAxeNumberChange(axe: 1 | 2): void {
		this.props.onChange({ ...this.props.settings, axe })
	}

	private handleBudgetChange(budget: number): void {
		this.props.onChange({ ...this.props.settings, budget })
	}

	private handleClickTestConnection(): void {
		this.setState({ error: null, fetchingCollectivites: true }, () => {
			this.forceFetchingCollectivites(this.props)
		})
	}

	private handleCollectiviteChange(idCollectiviteGF: number): void {
		this.props.onChange({ ...this.props.settings, idCollectiviteGF })
	}

	private handleServerAddressChange(server: string): void {
		this.abortController.abort()
		this.setState({ connectionStatus: null }, () => {
			this.props.onChange({ ...this.props.settings, server })
		})
	}

	private handleServerPortChange(port: number): void {
		this.abortController.abort()
		this.setState({ connectionStatus: null }, () => {
			this.props.onChange({ ...this.props.settings, port })
		})
	}

	private renderConnectionButton(): React.ReactNode {
		return <div className="buttons has-addons is-right">
			<button
			className={cl("button", { "is-loading": this.state.fetchingCollectivites })}
			disabled={this.shoudlDisableConnection()}
			type="button"
			onClick={() => this.handleClickTestConnection()}>
				{this.renderConnectionButtonIcon()}
				<span>Tester la connexion</span>
			</button>
		</div>
	}

	private renderConnectionButtonIcon(): React.ReactNode {
		if (this.state.fetchingCollectivites || !this.state.connectionStatus)
			return null

		let icon: React.ReactNode = <FontAwesomeIcon icon="times" color="red"/>

		if (this.state.connectionStatus === ConnectionStatus.Success)
			icon = <FontAwesomeIcon icon="check" color="green"/>

		return <span className="icon">{icon}</span>
	}

	private renderError(): React.ReactNode {
		if (!this.state.error) return

		const message = `Connexion au serveur Berger Levrault impossible :`
		const detail = this.state.error instanceof Error
		? this.state.error.message
		: this.state.error

		return <Notification
		detail={detail}
		level={NotificationLevel.Danger}
		light
		message={message}
		onClickCloseButton={() => this.setState({ error: null })}/>
	}

	private shoudlDisableConnection() {
		return !(this.props.settings.server && this.props.settings.port)
	}
}

enum ConnectionStatus {
	Undefined,
	Success,
	Fail
}

interface Props {
	loading: boolean;
	settings: Model.BergerLevrault;
	onChange(settings: Model.BergerLevrault): void;
}

interface State {
	collectivites: Model.BergerlevraultLocalauthority[];
	connectionStatus: ConnectionStatus | null;
	error: any;
	fetchingCollectivites: boolean;
}



// WEBPACK FOOTER //
// ./src/component/page/local-authority-detail-page/connector-field/berger-levrault-connector/index.tsx